import { Search, Check, X, MoreHorizontal, FileStack } from "lucide-react";
import { useCallback, useState, useEffect, useMemo } from "react";
import useViews from "@/react/hooks/useViews";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { cn } from "@/modern/utils";
import { WorkFilesProvider, useWorkFilesContext } from "./context/WorkFilesContext";
import { WorkFileCard } from "./components/WorkFileCard";
import { WorkFileDetailView } from "./components/WorkFileDetailView";
import { LinkedPagesPanel } from "./components/LinkedPagesPanel";

const VISIBLE_CHIPS = 4;

interface ChipProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const Chip = ({ active, onClick, children }: ChipProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "inline-flex h-7 items-center rounded-full border px-3 text-xs font-medium",
      "transition-[color,background-color,border-color,box-shadow] duration-150",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-1",
      active
        ? "pointer-events-none border-foreground bg-foreground text-background shadow-sm"
        : "border-border/60 bg-background/70 text-muted-foreground hover:border-foreground/30 hover:bg-background hover:text-foreground",
    )}
  >
    {children}
  </button>
);

const WorkFilesViewContent = () => {
  const {
    workFiles,
    filtered,
    problemHubs,
    workfileCards,
    allPages,
    pageCountByWorkFileId,
    query,
    setQuery,
    activeHubId,
    setActiveHubId,
    onArchiveFile,
    onUnarchiveFile,
    onDeleteFile,
  } = useWorkFilesContext();

  const viewManagerState = useViews();

  const [selectedWorkFileId, setSelectedWorkFileId] = useState<number | null>(null);
  const [panelFileId, setPanelFileId] = useState<number | null>(null);

  const detailFile = selectedWorkFileId
    ? workFiles.find((f) => f.id === selectedWorkFileId) ?? null
    : null;

  const panelFile = panelFileId
    ? workFiles.find((f) => f.id === panelFileId) ?? null
    : null;

  const panelPages = useMemo(
    () => allPages.filter((p) => p.workFile === panelFileId),
    [allPages, panelFileId],
  );

  const openWorkFile = useCallback((id: number) => setSelectedWorkFileId(id), []);
  const closeWorkFile = useCallback(() => setSelectedWorkFileId(null), []);
  const selectForPanel = useCallback((id: number) => {
    setPanelFileId((prev) => (prev === id ? null : id));
  }, []);

  // When navigating here from a banner click with a specific work file ID, open it directly.
  useEffect(() => {
    const workFileId = viewManagerState?.selectedView?.otherParams?.workFileId;
    if (workFileId) {
      openWorkFile(Number(workFileId));
    }
  }, [viewManagerState?.selectedView?.otherParams?.workFileId]);

  if (detailFile) {
    return (
      <WorkFileDetailView
        file={detailFile}
        cards={workfileCards}
        onBack={closeWorkFile}
      />
    );
  }

  const visibleHubs = problemHubs.slice(0, VISIBLE_CHIPS);
  const overflowHubs = problemHubs.slice(VISIBLE_CHIPS);
  const activeOverflowHub = overflowHubs.find((h) => h.id === activeHubId) ?? null;
  const activeHub = problemHubs.find((h) => h.id === activeHubId) ?? null;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Search + filters bar — full width */}
      <div className="flex shrink-0 items-center gap-4 border-b border-border bg-accent px-5 min-h-[3rem]">
        {/* Search — fixed width, X button contained inside */}
        <div className="relative w-52 shrink-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-3 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search work files…"
            variant="background"
            inputSize="sm"
            className={cn(
              "h-8 w-full rounded-full border border-border bg-card py-0 pl-8 text-xs leading-none text-card-foreground md:text-xs",
              "placeholder:text-muted-foreground",
              "!ring-0 !ring-offset-0 focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/20",
              query ? "pr-8" : "pr-3.5",
            )}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>


        {/* Tags — fill remaining space */}
        {problemHubs.length > 0 && (
          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
            {activeHub && (
              <button
                type="button"
                onClick={() => setActiveHubId(null)}
                className="ml-1 text-xs text-muted-foreground transition-colors hover:text-foreground mr-3"
              >
                Clear
              </button>
            )}
            <Chip
              active={activeHubId === null}
              onClick={() => setActiveHubId(null)}
            >
              All
            </Chip>
            {visibleHubs.map((hub) => (
              <Chip
                key={hub.id}
                active={hub.id === activeHubId}
                onClick={() =>
                  setActiveHubId(hub.id === activeHubId ? null : (hub.id as number))
                }
              >
                {hub.label}
              </Chip>
            ))}
            {overflowHubs.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    className={cn(
                      "h-8 w-8 shrink-0 rounded-full border p-0 shadow-none transition-[color,box-shadow,background-color,border-color]",
                      activeOverflowHub
                        ? "border-transparent bg-primary/15 text-foreground ring-1 ring-primary/20"
                        : "border-border/35 bg-card text-muted-foreground hover:border-border/55 hover:bg-accent/35 hover:text-foreground",
                    )}
                    aria-label="More filter options"
                  >
                    <MoreHorizontal className="size-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={6} className="w-48 p-1.5">
                  <div className="flex flex-col gap-0.5">
                    {overflowHubs.map((hub) => {
                      const checked = hub.id === activeHubId;
                      return (
                        <button
                          key={hub.id}
                          type="button"
                          onClick={() =>
                            setActiveHubId(checked ? null : (hub.id as number))
                          }
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs transition-colors",
                            checked
                              ? "bg-primary/10 font-medium text-foreground"
                              : "font-medium text-muted-foreground hover:bg-accent/40 hover:text-foreground",
                          )}
                        >
                          {checked ? (
                            <Check className="size-3 shrink-0 text-primary" />
                          ) : (
                            <span className="size-3 shrink-0" />
                          )}
                          <span className="line-clamp-1">{hub.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            )}

          </div>
        )}
      </div>

      {/* Content row: card grid + optional pages panel */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Card grid */}
        <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="min-h-full flex flex-col">
        {filtered.length === 0 && query === "" && activeHubId === null ? (
          <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden px-6 py-12 text-center">
            {/* Ghost cards — absolute backdrop, out of flow so they don't shift the centered CTA */}
            <div className="pointer-events-none select-none absolute inset-x-0 top-0 px-6" aria-hidden>
              <div className="mx-auto w-full max-w-xl">
                <div className="grid grid-cols-3 gap-3 opacity-[0.18]">
                  {[
                    { badge: "General Repair Need", lines: [70, 90, 55], hasThumb: true },
                    { badge: "Recurring Condition",  lines: [80, 60, 75], hasThumb: false },
                    { badge: "Active Failure",       lines: [65, 85, 50], hasThumb: true },
                  ].map(({ badge, lines, hasThumb }, i) => (
                    <div key={i} className="flex flex-col gap-2.5 rounded-xl border border-border bg-card p-3.5">
                      <div className="flex items-center justify-between">
                        <div className="h-4 rounded-md bg-muted" style={{ width: `${badge.length * 5.5}px`, maxWidth: "80%" }} />
                        <div className="h-3.5 w-3.5 rounded bg-muted/60" />
                      </div>
                      <div className="h-3.5 rounded bg-muted" style={{ width: `${lines[0]}%` }} />
                      <div className="space-y-1.5">
                        <div className="h-2.5 rounded bg-muted/70" style={{ width: `${lines[1]}%` }} />
                        <div className="h-2.5 rounded bg-muted/70" style={{ width: `${lines[2]}%` }} />
                      </div>
                      {hasThumb ? (
                        <div className="mt-0.5 grid grid-cols-3 gap-1">
                          {[...Array(3)].map((_, j) => (
                            <div key={j} className="aspect-square rounded-md bg-muted/80" />
                          ))}
                        </div>
                      ) : (
                        <div className="mt-0.5 flex h-12 items-center justify-center rounded-md border border-dashed border-border/60 bg-muted/30" />
                      )}
                      <div className="flex items-center gap-2 border-t border-border/60 pt-2">
                        <div className="h-2.5 w-14 rounded bg-muted/50" />
                        <div className="h-2.5 w-16 rounded bg-muted/50" />
                        <div className="ml-auto h-2.5 w-8 rounded bg-muted/40" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/60 to-background" />
              </div>
            </div>

            {/* CTA — only in-flow content so justify-center truly centers it */}
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-muted text-muted-foreground mb-4">
              <FileStack className="h-5 w-5" />
            </div>
            <h3 className="text-base font-medium text-foreground">No work files yet</h3>
            <p className="mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">
              Work files will appear here once they are generated from your problem hubs.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/50 m-4 p-12 text-center">
            <p className="text-sm font-medium text-foreground">No work files match</p>
            <p className="text-xs text-muted-foreground">
              Try adjusting your search or clearing filters.
            </p>
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setActiveHubId(null);
              }}
              className="mt-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
            >
              Clear all
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((file) => (
              <WorkFileCard
                key={file.id}
                file={file}
                cards={workfileCards}
                pageCount={pageCountByWorkFileId.get(file.id) ?? 0}
                isSelected={panelFileId === file.id}
                onSelect={selectForPanel}
                onOpen={openWorkFile}
                onArchive={onArchiveFile}
                onUnarchive={onUnarchiveFile}
                onDelete={onDeleteFile}
              />
            ))}
          </div>
        )}
        </div>{/* end min-h-full wrapper */}
        </div>{/* end overflow-y-auto */}

        {/* Published pages — appears when a card is selected */}
        {panelFile && (
          <div className="hidden lg:flex w-[300px] shrink-0 flex-col overflow-hidden border-l border-border p-3">
            <div className="flex flex-col h-full overflow-hidden rounded-xl border border-border shadow-sm">
              <LinkedPagesPanel
                pages={panelPages}
                headerTitle={panelFile.displayName}
                onClose={() => setPanelFileId(null)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const WorkFilesView = () => {
  return (
    <WorkFilesProvider>
      <WorkFilesViewContent />
    </WorkFilesProvider>
  );
};

export default WorkFilesView;
